import { numberFormat } from "shared/util"

import { Box, Divider, List, ListItem, ListItemButton, ListItemButtonProps, Stack, StackProps, Typography } from "@mui/material"
import {
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon
} from '@mui/icons-material'

import { Section } from "components/controls/section"

import { ResearcherResult, ResearchProjectResult, ResearchResult } from "interface/research"
import { useResearchPreview } from "features/research/preview/container"
import { useEffect, useMemo, useState } from "react"
import { ValueSpan } from "components/controls/valueSpan"
import { ClampTypography } from "components/controls/clampTypography"
import { BlockCaption } from "components/controls/blockCaption"

const isValidNumber = (...arr: (number | null | undefined)[]) => arr.some(n => (
	n === undefined || n === null || isNaN(n) || n === 0
) ? false : true)

const isValidString = (...arr: (string | null | undefined)[]) => arr.some(s => (
	s === undefined || s === null || s.trim() === ''
) ? false : true)

type ResearchCostsProps = {
	researchs: ResearchResult[]
	totalCost: number
}
const ResearchCosts = ({
	researchs,
	totalCost
}: ResearchCostsProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const costs = useMemo(
		() => researchs.flatMap(({ nendo, researchCost }) => {
			if (researchCost === null || researchCost === 0) return []
			return { nendo, researchCost }
		}),
		[researchs]
	)
	useEffect(
		() => {
			setIsOpen(false)
		},
		[researchs]
	)
	return costs.length > 0 ? (
		<Section labelWidth="8em" label='研究費'>
			{isOpen ? (
				<Stack direction="row" spacing={1} alignItems="flex-start">
					<Stack direction="column">
						{costs.map(({ nendo, researchCost }) => (
							<Stack key={`researchCost-${nendo}`} direction="row" spacing={1} alignItems="center">
								<ValueSpan text={`${nendo}`} suffix='年度'/>
								<ValueSpan text={numberFormat(researchCost)} suffix='円'/>
							</Stack>
						))}
					</Stack>
					<Box sx={{ cursor: "pointer" }} onClick={() => setIsOpen(false)}>
						<ExpandLessIcon fontSize="inherit"/>
					</Box>
				</Stack>
			) : (
				<Stack direction="row" spacing={1} alignItems="flex-start">
					<Stack direction="row" spacing={1} alignItems="center">
						{(costs.length > 1) ? (
							<>
								<ValueSpan text={numberFormat(totalCost)} prefix='計' suffix='円'/>
								<ValueSpan text={numberFormat(costs.length)} suffix='件'/>
							</>
						) : (
							<ValueSpan text={numberFormat(totalCost)} suffix='円'/>
						)}
					</Stack>
					{costs.length > 1 ? (
						<Box sx={{ cursor: "pointer" }} onClick={() => setIsOpen(true)}>
							<ExpandMoreIcon fontSize="inherit"/>
						</Box>
					) : <></>}
				</Stack>
			)}
		</Section>
	) : <></>
}

type ChangeInfosProps = {
	researchs: ResearchResult[]
}
const ChangeInfos = ({
	researchs
}: ChangeInfosProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const changeInfos = useMemo(
		() => researchs.flatMap(({ nendo, changeInfo }) => {
			if (changeInfo === null || changeInfo === "") return []
			return { nendo, changeInfo }
		}),
		[researchs]
	)
	useEffect(
		() => {
			setIsOpen(false)
		},
		[researchs]
	)
	return changeInfos.length > 0 ? (
		<Section labelWidth="8em" label='研究者/施設変更'>
			{isOpen ? (
				<Stack direction="row" spacing={1} alignItems="flex-start">
					<Stack direction="column">
						{changeInfos.map(({ nendo, changeInfo }) => (
							<Stack key={`chjangeInfo-${nendo}`} direction="row" spacing={1} alignItems="flex-start">
								<ValueSpan text={`${nendo}`} suffix='年度'/>
								<ClampTypography text={changeInfo} marginX={1}/>
							</Stack>
						))}
					</Stack>
					<Box sx={{ cursor: "pointer" }} onClick={() => setIsOpen(false)}>
						<ExpandLessIcon fontSize="inherit"/>
					</Box>
				</Stack>
			) : (
				<Stack direction="row" spacing={1} alignItems="flex-start">
					<Stack direction="row" spacing={1} alignItems="center">
						<ValueSpan text={numberFormat(changeInfos.length)} suffix='件'/>
					</Stack>
					<Box sx={{ cursor: "pointer" }} onClick={() => setIsOpen(true)}>
						<ExpandMoreIcon fontSize="inherit"/>
					</Box>
				</Stack>
			)}
		</Section>
	) : <></>
}

type ResearcherListItemProps = {
	researcher: ResearcherResult
	targetKojinCode?: string
	onKojinOpen: (kojinCode: string) => void
	targetSisetuCode?: string
	onSisetuOpen: (sisetuCode: string) => void
} & ListItemButtonProps
const ResearcherListItem = ({
	researcher,
	targetKojinCode,
	onKojinOpen,
	targetSisetuCode,
	onSisetuOpen,
	...others
}: ResearcherListItemProps) => {
	return (
		<ListItem disablePadding>
			<ListItemButton
				{...others}
				disabled={(researcher.kojinCode || researcher.facility?.sisetuCode) ? false : true}
				onClick={() => {
					if (researcher.kojinCode) {
						onKojinOpen(researcher.kojinCode)
					} else if (researcher.facility?.sisetuCode) {
						onSisetuOpen(researcher.facility.sisetuCode)
					}
				}}
				sx={{
					paddingX: 2,
					paddingY: 0.5
				}}
			>
				<Stack direction="column" width='100%'>
					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
						<Typography
							variant="body2"
							fontWeight={researcher.kojinCode === targetKojinCode ? "bold" : undefined}
							color={(researcher.kojinCode === null && researcher.facility?.sisetuCode) ? "gray" : undefined}
						>
							{researcher.kojinName}
						</Typography>
						<Typography
							variant="body2"
							fontWeight={(targetSisetuCode && researcher.facility?.sisetuCode === targetSisetuCode) ? "bold" : undefined}
							color={(researcher.kojinCode || researcher.facility?.sisetuCode) ? undefined : "gray"}
						>
							{researcher.affiliation}
						</Typography>
					</Stack>
				</Stack>
			</ListItemButton>
		</ListItem>
	)
}

type ResearchPreviewBodyProps = {
	project: ResearchProjectResult
	container: ReturnType<typeof useResearchPreview>
} & StackProps
export const ResearchPreviewBody = ({
	project,
	container: {
		researchs,
		currentResearch: current,
		targetKojinCode,
		targetSisetuCode,
		onKojinOpen,
		onSisetuOpen
	},
	...others
}: ResearchPreviewBodyProps) => {
	return (
		<Stack {...others} direction="column">
			{current && (
				<>
					<Box>
						<BlockCaption caption="基本情報" rightElement={
							<Stack direction="row" spacing={1} alignItems="center">
								<Stack direction="row" spacing={0.5} alignItems="center">
									<Typography variant="caption" color="textSecondary">文献番号</Typography>
									<Typography variant="body2">{current.bunkenNo}</Typography>
								</Stack>
								{current.kadaiNo && (
									<Stack direction="row" spacing={0.5} alignItems="center">
										<Typography variant="caption" color="textSecondary">課題番号</Typography>
										<Typography variant="body2">{current.kadaiNo}</Typography>
									</Stack>
								)}
							</Stack>
						}/>

						{isValidString(current.kubunName, current.bunyaName, current.jigyouName) && (
							<Section labelWidth="8em" label='区分'>
								<Stack direction="row" spacing={1} alignItems="center">
									{isValidString(current.bunyaName) && (
										<Typography variant="body2">{current.kubunName}</Typography>
									)}
									{isValidString(current.bunyaName) && (
										<Typography variant="body2">{current.bunyaName}</Typography>
									)}
									{isValidString(current.jigyouName) && (
										<Typography variant="body2">{current.jigyouName}</Typography>
									)}
								</Stack>
							</Section>
						)}
						{project.startNendo ? (
							project.startNendo === project.endNendo ? (
								<Section labelWidth="8em" label='期間'>
									<Stack direction="row" spacing={0.5} alignItems="center">
										<Typography variant="body2">{project.startNendo}</Typography>
										<Typography variant="caption" color="textSecondary">年度（単年）</Typography>
									</Stack>
								</Section>
							) : (
								<Section labelWidth="8em" label='期間'>
									<Stack direction="row" spacing={0.5} alignItems="center">
										<Typography variant="body2">{current.startNendo}</Typography>
										<Typography variant="caption" color="textSecondary">～</Typography>
										<Typography variant="body2">{current.endNendo}</Typography>
										<Typography variant="caption" color="textSecondary">年度</Typography>
									</Stack>
								</Section>
							)
						) : <></>}

						<ResearchCosts researchs={researchs} totalCost={project.totalCost}/>
						<ChangeInfos researchs={researchs}/>

						{current.primaryResearcher && (
							<Section labelWidth="8em" label='責任者' disablePaddingX labelProps={{ py: 1.5 }}>
								<List disablePadding>
									<ResearcherListItem 
										researcher={current.primaryResearcher}
										onKojinOpen={onKojinOpen}
										targetKojinCode={targetKojinCode}
										onSisetuOpen={onSisetuOpen}
										targetSisetuCode={targetSisetuCode}
									/>
								</List>
							</Section>
						)}
						{current.supportResearchers.length > 0 && (
							<Section labelWidth="8em" label='分担者' disablePaddingX labelProps={{ py: 1.5 }}>
								<List disablePadding>
									{current.supportResearchers.map((researcher, i) =>
										<ResearcherListItem 
											key={`person-${i}-${researcher.kojinCode ?? 'unknown'}`}
											researcher={researcher}
											onKojinOpen={onKojinOpen}
											targetKojinCode={targetKojinCode}
											onSisetuOpen={onSisetuOpen}
											targetSisetuCode={targetSisetuCode}
										/>
									)}
								</List>
							</Section>
						)}
					</Box>

					{isValidString(
						current.abstract,
						current.purpose,
						current.method,
						current.results,
						current.conclusion
					) && (
						<Box>
							<BlockCaption caption="研究概要"/>

							{isValidString(current.abstract) && (
								<Box paddingX={2} paddingY={1}>
									<ClampTypography text={current.abstract}/>
								</Box>
							)}
							{isValidString(current.purpose) && (
								<Section labelWidth="8em" label='目的'>
									<ClampTypography text={current.purpose}/>
								</Section>
							)}
							{isValidString(current.method) && (
								<Section labelWidth="8em" label='方法'>
									<ClampTypography text={current.method}/>
								</Section>
							)}
							{isValidString(current.results) && (
								<Section labelWidth="8em" label='結果と考察'>
									<ClampTypography text={current.results}/>
								</Section>
							)}
							{isValidString(current.conclusion) && (
								<Section labelWidth="8em" label='結論'>
									<ClampTypography text={current.conclusion}/>
								</Section>
							)}
						</Box>
					)}

					{(isValidString(
						current.politicalReportNo,
						current.academicAchievement,
						current.clinicalAchievement,
						current.guidelineDevelopment,
						current.otherAdminAchievement,
						current.otherImpact,
						current.application1, current.application2, current.application3, current.application4,
						current.acquisition1, current.acquisition2, current.acquisition3, current.acquisition4
					) || isValidNumber(
						current.articleCountJp, current.articleCountEn,
						current.otherArticleCountJp, current.otherArticleCountEn,
						current.conventionCountJp, current.conventionCountInternal,
						current.patentApplicationCount, current.patentAcquiredCount,
						current.policyReflectionCount, current.outreachActivityCount
					)) && (
						<Box>
							<BlockCaption caption="成果" rightElement={
								isValidString(current.politicalReportNo) ? (
									<Typography variant="body2">{current.politicalReportNo}</Typography>
								) : <></>
							}/>
							{isValidString(current.academicAchievement) && (
								<Section labelWidth="8em" label='専門/学術'>
									<ClampTypography text={current.academicAchievement}/>
								</Section>
							)}
							{isValidString(current.clinicalAchievement) && (
								<Section labelWidth="8em" label='臨床'>
									<ClampTypography text={current.clinicalAchievement}/>
								</Section>
							)}
							{isValidString(current.guidelineDevelopment) && (
								<Section labelWidth="8em" label='ガイドライン'>
									<ClampTypography text={current.guidelineDevelopment}/>
								</Section>
							)}
							{isValidString(current.otherAdminAchievement) && (
								<Section labelWidth="8em" label='行政'>
									<ClampTypography text={current.otherAdminAchievement}/>
								</Section>
							)}
							{isValidString(current.otherImpact) && (
								<Section labelWidth="8em" label='その他'>
									<ClampTypography text={current.otherImpact}/>
								</Section>
							)}

							{(isValidNumber(current.articleCountJp, current.articleCountEn)) && (
								<Section labelWidth="8em" label='原著論文'>
									<Stack direction="row" spacing={1}>
										{isValidNumber(current.articleCountJp) && (
											<ValueSpan text={numberFormat(current.articleCountJp)} prefix="和文" suffix="件"/>
										)}
										{isValidNumber(current.articleCountEn) && (
											<ValueSpan text={numberFormat(current.articleCountEn)} prefix="英文等" suffix="件"/>
										)}
									</Stack>
								</Section>
							)}

							{(isValidNumber(current.otherArticleCountJp, current.otherArticleCountEn)) && (
								<Section labelWidth="8em" label='その他論文'>
									<Stack direction="row" spacing={1}>
										{isValidNumber(current.otherArticleCountJp) && (
											<ValueSpan text={numberFormat(current.otherArticleCountJp)} prefix="和文" suffix="件"/>
										)}
										{isValidNumber(current.otherArticleCountEn) && (
											<ValueSpan text={numberFormat(current.otherArticleCountEn)} prefix="英文等" suffix="件"/>
										)}
									</Stack>
								</Section>
							)}

							{(isValidNumber(current.conventionCountJp, current.conventionCountInternal)) && (
								<Section labelWidth="8em" label='学会発表'>
									<Stack direction="row" spacing={1}>
										{isValidNumber(current.conventionCountJp) && (
											<ValueSpan text={numberFormat(current.conventionCountJp)} prefix="国内" suffix="件"/>
										)}
										{isValidNumber(current.otherArticleCountEn) && (
											<ValueSpan text={numberFormat(current.conventionCountInternal)} prefix="国際等" suffix="件"/>
										)}
									</Stack>
								</Section>
							)}

							{(
								isValidNumber(current.patentApplicationCount, current.patentAcquiredCount) || 
								isValidString(current.application1, current.application2, current.application3, current.application4) ||
								isValidString(current.acquisition1, current.acquisition2, current.acquisition3, current.acquisition4)
							) && (
								<Section labelWidth="8em" label='特許'>
									<Stack direction="row" spacing={1}>
										{isValidNumber(current.patentApplicationCount) && (
											<ValueSpan text={numberFormat(current.patentApplicationCount)} prefix="出願" suffix="件"/>
										)}
										{isValidNumber(current.patentAcquiredCount) && (
											<ValueSpan text={numberFormat(current.patentAcquiredCount)} prefix="取得" suffix="件"/>
										)}
									</Stack>
									{[current.application1, current.application2, current.application3, current.application4].flatMap((application, i) => isValidString(application) ? (
										<Stack key={`application-${i + 1}`} direction="row" spacing={1} alignItems="flex-start">
											<Typography variant="body2">出願{i+1}</Typography>
											<ClampTypography text={application} marginX={1}/>
										</Stack>
									) : [])}
									{[current.acquisition1, current.acquisition2, current.acquisition3, current.acquisition4].flatMap((acquisition, i) => isValidString(acquisition) ? (
										<Stack key={`acquisition-${i + 1}`} direction="row" spacing={1} alignItems="flex-start">
											<Typography variant="body2">取得{i+1}</Typography>
											<ClampTypography text={acquisition} marginX={1}/>
										</Stack>
									) : [])}
								</Section>
							)}

							{isValidNumber(current.policyReflectionCount) && (
								<Section labelWidth="8em" label='施策への反映'>
									<ValueSpan text={numberFormat(current.policyReflectionCount)} suffix="件"/>
								</Section>
							)}
							{isValidNumber(current.outreachActivityCount) && (
								<Section labelWidth="8em" label='普及/啓発活動'>
									<ValueSpan text={numberFormat(current.outreachActivityCount)} suffix="件"/>
								</Section>
							)}
						</Box>
					)}

					{(isValidNumber(
						current.subsidyGranted,
						current.subsidyConfirmed,
						current.subsidyBalance,
						current.expOldDirectPersonnel,
						current.expOldDirectHonoraria,
						current.expOldDirectTravel,
						current.expOldDirectResearch,
						current.expOldConsignment,
						current.expOldIndirect,
						current.expNewDirectEquipment,
						current.expNewDirectPersonnelHonoraria,
						current.expNewDirectTravel,
						current.expNewDirectOther,
						current.expNewIndirect,
						current.expTotal
					) || isValidString(current.remarks)) && (
						<Box>
							<BlockCaption caption="収支"/>
							
							{isValidNumber(
								current.subsidyGranted,
								current.subsidyConfirmed,
								current.subsidyBalance
							) && (
								<Section labelWidth="8em" label='補助金収入'>
									<Stack direction="row" spacing={1}>
										{isValidNumber(current.subsidyGranted) ? (
											(current.subsidyGranted === current.subsidyConfirmed) ? (
												<ValueSpan text={numberFormat(current.subsidyGranted)} suffix="円"/>
											) : (
												<>
													<ValueSpan prefix="交付額" text={numberFormat(current.subsidyGranted)} suffix="円"/>
													{isValidNumber(current.subsidyConfirmed) && (
														<ValueSpan prefix="－ 確定額" text={numberFormat(current.subsidyConfirmed)} suffix="円"/>
													)}
													{isValidNumber(current.subsidyBalance) && (
														<ValueSpan prefix="＝ 差引額" text={numberFormat(current.subsidyBalance)} suffix="円"/>
													)}
												</>
											)
										) : isValidNumber(current.subsidyConfirmed) ? (
											<ValueSpan text={numberFormat(current.subsidyGranted)} suffix="円"/>
										) : <></>}
									</Stack>
								</Section>
							)}
							{isValidNumber(
								current.expOldDirectPersonnel,
								current.expOldDirectHonoraria,
								current.expOldDirectTravel,
								current.expOldDirectResearch,
								current.expOldConsignment,
								current.expOldIndirect,
								current.expNewDirectEquipment,
								current.expNewDirectPersonnelHonoraria,
								current.expNewDirectTravel,
								current.expNewDirectOther,
								current.expNewIndirect
							) ? (
								<Section labelWidth="8em" label='支出内訳'>
									<div>
										<table style={{ tableLayout: "fixed", width: "auto", borderCollapse: "collapse" }}>
											<tbody>
												{isValidNumber(current.expOldDirectPersonnel) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>人件費</Typography>
														<td><ValueSpan text={numberFormat(current.expOldDirectPersonnel)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expOldDirectHonoraria) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>諸謝金</Typography>
														<td><ValueSpan text={numberFormat(current.expOldDirectHonoraria)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expOldDirectTravel) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>旅費</Typography>
														<td><ValueSpan text={numberFormat(current.expOldDirectTravel)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expOldDirectResearch) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>調査研究費</Typography>
														<td><ValueSpan text={numberFormat(current.expOldDirectResearch)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expOldConsignment) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>委託費</Typography>
														<td><ValueSpan text={numberFormat(current.expOldConsignment)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expOldIndirect) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>間接経費</Typography>
														<td><ValueSpan text={numberFormat(current.expOldIndirect)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expNewDirectEquipment) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>物品費</Typography>
														<td><ValueSpan text={numberFormat(current.expNewDirectEquipment)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expNewDirectPersonnelHonoraria) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>人件費・謝金</Typography>
														<td><ValueSpan text={numberFormat(current.expNewDirectPersonnelHonoraria)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expNewDirectTravel) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>旅費</Typography>
														<td><ValueSpan text={numberFormat(current.expNewDirectTravel)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expNewDirectOther) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>その他研究費</Typography>
														<td><ValueSpan text={numberFormat(current.expNewDirectOther)} suffix="円"/></td>
													</tr>
												)}
												{isValidNumber(current.expNewIndirect) && (
													<tr>
														<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>間接経費</Typography>
														<td><ValueSpan text={numberFormat(current.expNewIndirect)} suffix="円"/></td>
													</tr>
												)}
											</tbody>
											<tfoot>
												{isValidNumber(current.expTotal) && (
													<>
														<tr><td colSpan={2}><Divider/></td></tr>
														<tr>
															<Typography component="th" variant="caption" color="text.secondary" textAlign="left" paddingRight={1}>合計</Typography>
															<td><ValueSpan text={numberFormat(current.expTotal)} suffix="円"/></td>
														</tr>
													</>
												)}
											</tfoot>
										</table>
									</div>
								</Section>
							) : isValidNumber(current.expTotal) ? (
								<Section labelWidth="8em" label='支出'>
									<ValueSpan text={numberFormat(current.expTotal)} suffix="円"/>
								</Section>
							) : <></>}

							{isValidString(current.remarks) && (
								<Section labelWidth="8em" label='備考'>
									<ClampTypography text={current.remarks}/>
								</Section>
							)}
						</Box>
					)}
				</>
			)}
			<Section labelWidth="8em" flexGrow={1}/>
		</Stack>
	)
}